import { useEffect, useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const otherPopupOpen = () => document.querySelector('[role="dialog"],[data-testid="lucky-wheel-modal"],[data-testid="level-up-modal"]');

export function EmailConsentPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [c, setC] = useState({ newsletter: false, winback: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let iv;
    const t = setTimeout(() => api.get("/profile/email-consent").then(({ data }) => {
      if (data.asked) return;
      iv = setInterval(() => { if (!otherPopupOpen()) { clearInterval(iv); setOpen(true); } }, 1500);
    }).catch(() => {}), 4000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (vals) => {
    setBusy(true);
    try {
      await api.put("/profile/email-consent", vals);
      toast.success(vals.newsletter || vals.winback ? "Danke! Wir melden uns per E-Mail 💌" : "Alles klar – du bekommst Neuigkeiten nur per Push 🔔");
      setOpen(false);
      window.dispatchEvent(new Event("email-consent-changed"));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  if (!open) return null;
  const row = (k, title, text) => (
    <label className="flex items-start gap-3 p-3 rounded-2xl bg-rose-50/70 cursor-pointer">
      <div className="flex-1 min-w-0"><p className="font-bold text-sm">{title}</p><p className="text-xs text-slate-500">{text}</p></div>
      <Switch checked={c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} data-testid={`consent-${k}-switch`} className="data-[state=unchecked]:bg-slate-300" />
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && setOpen(false)}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="email-consent-dialog">
        <span className="w-12 h-12 rounded-2xl pink-gradient text-white flex items-center justify-center"><Mail className="w-6 h-6" /></span>
        <DialogTitle className="font-display text-2xl font-extrabold">Dürfen wir dir E-Mails schicken?</DialogTitle>
        <DialogDescription className="text-sm text-slate-600">Wähle, welche E-Mails du von uns bekommen möchtest. Ohne Zustimmung informieren wir dich nur per Push. Du kannst das jederzeit im Profil ändern.</DialogDescription>
        <div className="space-y-2">
          {row("newsletter", "Wochenrückblick", "Jeden Sonntag: dein Punktestand, neue Prämien und Top-Beiträge.")}
          {row("winback", "„Wir vermissen dich“", "Eine liebe Erinnerung mit Rückkehr-Bonus, wenn du 14 Tage nicht da warst.")}
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={() => save({ newsletter: false, winback: false })} disabled={busy} data-testid="consent-decline" className="flex-1 h-11 rounded-full bg-slate-100 font-semibold text-sm text-slate-600">Nein, nur Push</button>
          <button onClick={() => save(c)} disabled={busy} data-testid="consent-save" className="flex-1 h-11 rounded-full pink-gradient text-white font-bold text-sm inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Speichern</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
